import { formatMarketCap, formatMemberships, formatSector, inputColumnLabel } from './format';

// The table's column model.
//
// Which columns a response can carry is a real question — PIT enrichment arrived, single-input feeds
// have no markers, and every column beyond the floor exists in the response only because the request
// asked for it by name — so the table asks this module instead of hardcoding a header row.
//
// Every function here takes the normalized `result` from normalizeViewResponse rather than loose
// `(rows, memberColumns)` arguments. That is deliberate: `availableColumns` is a per-feed universe,
// and passing the whole result is what carries it to every call site instead of leaving the callers
// to remember a fourth argument.

// The floor: /feed/view emits these on every read whether or not `columns` names them, and naming
// one is a documented no-op. Hiding them is still a display choice the table can honour — the two
// are different questions, and conflating them is why the chooser used to offer only three columns.
export const ALWAYS_COLUMN_FIELDS = ['utc_knowable_at', 'score', 'ticker', 'company_id'];

// What read #1 of an unseen feed can name. Its `available_columns` is only knowable from a response,
// so the first request guesses — and it can only guess at names every feed plausibly has. Index
// flags and spec-declared payload names are unguessable, so they wait for the second read.
export const SPECULATIVE_COLUMNS = ['sector', 'market_cap', 'company_name'];

// Always rendered from fields the response always carries.
//
// `width` is the column's grid track. It lives here rather than in the table because the table used
// to keep a second, parallel list of these same three keys purely to look their widths up — two
// declarations of one set, either of which could grow a column the other didn't know about.
// ORDER IS THE CONTRACT. tableGrid builds its grid tracks from this array, and the table now emits
// its cells from it too — they used to disagree, because the cells were hardcoded in the renderer
// while the tracks came from here. Reordering this list was a one-line edit that produced a table
// whose every core column rendered under a width meant for a different one, and it read as a CSS
// problem rather than as what it was.
//
// `frozen` marks the identity column, which sticks horizontally so the middle columns scroll under
// it. `sortField` defaults to the key and is spelled out nowhere here because no core column sorts
// by anything but itself — it exists so core, optional and member columns answer one question the
// same way.
export const CORE_COLUMNS = [
  { key: 'company', label: 'Company', minWidth: 200, grow: 2, frozen: true },
  { key: 'score', label: 'Score', minWidth: 90, grow: 0.8 },
  // Derived by attachPreviousScores from the rows of this very response — neither has a name on the
  // wire, and neither may ever be put in a `columns` request: that list is an allowlist over the
  // feed's own universe, so an invented name is a 422 the page then caches as a column refusal for
  // the whole session.
  //
  // CORE rather than OPTIONAL, and not by preference. availableOptionalColumns gates an optional key
  // on the server's universe naming it, or on the rows carrying its wire field; a derived key can
  // never satisfy either, so an optional entry would be offered by the chooser and never drawn.
  //
  // They share Score's track so the three read as one comparison. 'Change' rather than 'Δ' because
  // the header IS the sort button and sortStateLabel builds its accessible name from this same
  // label — a visible 'Δ' either reads aloud as nothing or disagrees with its accessible name.
  { key: 'prevScore', label: 'Prev score', minWidth: 90, grow: 0.8 },
  { key: 'scoreDelta', label: 'Change', minWidth: 90, grow: 0.8 },
  // Derived too, and by the same rules — scoreSeries builds it from the rows of this very response,
  // so it can never be named in a `columns` request either. What separates it from the pair above is
  // the span it answers for: they compare this occasion with the one before it, and this one draws
  // every occasion the read holds for the company. That is why it survives aggregation, where an
  // average has no single predecessor and the pair go blank.
  //
  // Wider than the three numeric tracks because it holds a plot rather than a figure, and the plot
  // has to stay legible at the compact density.
  { key: 'movement', label: 'Movement', minWidth: 132, grow: 1 },
  { key: 'knowableAt', label: 'Knowable at (UTC)', minWidth: 150, grow: 1.2 },
];

// Every member column is the same shape of thing — a marker and maybe a value — so they share one
// track rather than declaring one apiece.
// Every member column is the same size of thing, so they share one track rather than each carrying
// its own.
export const MEMBER_COLUMN_TRACK = { minWidth: 130, grow: 1 };

// Only reachable if a column is added below without a track. Sized like Sector, the most ordinary of
// them, so a forgotten width is a slightly wrong column rather than a collapsed one.
const DEFAULT_COLUMN_TRACK = { minWidth: 120, grow: 1 };

// A column's track, as CSS. The pair is the source of truth and the string is generated from it —
// the other way round is how this went wrong: tableGrid used to recover the floor by regex-parsing
// its own output, `Number(/minmax\((\d+)px/.exec(track)?.[1]) || 0`, which silently yields ZERO for
// any track not written in exactly that shape. Rewrite one as `minmax(5rem, 1fr)` or a `clamp()`
// during a restyle and that column stops being counted, the scroll container under-reserves, and
// nothing anywhere says so.
const trackCss = ({ minWidth, grow }) => `minmax(${minWidth}px, ${grow}fr)`;

// A column's own track, or the fallback for one declared without.
const trackOf = (column) =>
  typeof column?.minWidth === 'number' ? { minWidth: column.minWidth, grow: column.grow ?? 1 } : DEFAULT_COLUMN_TRACK;

// The table's grid gap and row padding, here rather than only in the styled-component, because the
// min-width computed below has to agree with what the grid actually lays out.
export const GRID_GAP = 16;
export const ROW_PADDING = 8;

// The vertical halves of a row's padding, which the min-width arithmetic does not use but the rows
// do. Here beside their horizontal partner so a density change is one file's decision rather than a
// literal buried in a styled-component — and so the pair cannot be edited apart.
export const ROW_PADDING_COMPACT = 4;
export const ROW_PADDING_COMFORTABLE = 10;

// Member columns are named after the member, and namespaced so a member called "sector" can't
// collide with the enrichment column of that name.
export const MEMBER_COLUMN_PREFIX = 'member:';

export const memberColumnKey = (inputKey) => `${MEMBER_COLUMN_PREFIX}${inputKey}`;

export const isMemberColumnKey = (key) => String(key).startsWith(MEMBER_COLUMN_PREFIX);

// One index flag per index, named on the wire individually.
const INDEX_FIELD_PREFIX = 'is_member_of_';

const isIndexField = (name) => String(name).startsWith(INDEX_FIELD_PREFIX);

// The non-floor universe columns that are the FEED's own data rather than a score spec's declared
// payload: the point-in-time value columns and company_name. Together with the floor and the
// membership flags they are everything a request may name that `slim` does not take away.
//
// Same three names as SPECULATIVE_COLUMNS, and not the same idea. That list is "what read #1 of an
// unseen feed can guess at"; this one is "what survives slim". They coincide today because both are
// the universe minus the unguessable parts, and either could move without the other.
const NON_PAYLOAD_UNIVERSE_COLUMNS = ['sector', 'market_cap', 'company_name'];

// A name in a feed's universe that can only be a score spec's declared payload key — its
// `extra_columns`, the explanation and thinking behind a score. Identified by exclusion because the
// payload names are per-spec and unguessable, so there is no list of them to test against; what CAN
// be enumerated is everything they are not.
export const isPayloadName = (name) =>
  !ALWAYS_COLUMN_FIELDS.includes(name) && !NON_PAYLOAD_UNIVERSE_COLUMNS.includes(name) && !isIndexField(name);

// The columns the request can leave out, by their wire names. The is_member_of_<index> flags are
// also omittable but are handled as one group under the display key `memberships`: their real names
// aren't knowable until a response has been seen, and there can be a dozen of them.
//
// `sortField` is the field in rowSort's vocabulary that this column sorts by; Indices sorts by how
// many indices a row belongs to — see the note in rowSort.js.
export const OPTIONAL_COLUMNS = [
  { key: 'sector', field: 'sector', label: 'Sector', minWidth: 120, grow: 1, sortField: 'sector' },
  {
    key: 'market_cap',
    field: 'market_cap',
    label: 'Market cap',
    minWidth: 110,
    grow: 0.9,
    sortField: 'marketCap',
  },
  { key: 'memberships', field: null, label: 'Indices', minWidth: 160, grow: 1.4, sortField: 'memberships' },
];

export const OPTIONAL_COLUMN_KEYS = OPTIONAL_COLUMNS.map((column) => column.key);

// An empty list is a server (or a page) that never said, not a feed with an empty universe. That
// rule is shared with `requestedColumns` below and with the page's own per-feed store, because
// `[].length` erases the distinction everywhere and each place that loses it guesses wrongly in a
// different direction — a blank chooser here, a disabled retry there.
const asNames = (names) => (Array.isArray(names) && names.length ? names : null);

// What this feed *can* emit, per the server.
const universeOf = (result) => asNames(result?.availableColumns);

// What this particular read ASKED for. `columns` is additive, so a column the feed could have
// emitted is simply absent from these rows unless this list names it.
const requestedOf = (result) => asNames(result?.requestedColumns);

// Wire names to the display keys they stand behind. Both questions this module asks of a name list —
// what the feed can emit, and what this read named — are answered by the same mapping.
const displayKeysNaming = (names) => {
  const keys = new Set();
  (names || []).forEach((name) => {
    if (name === 'sector') keys.add('sector');
    if (name === 'market_cap') keys.add('market_cap');
    if (isIndexField(name)) keys.add('memberships');
  });
  return keys;
};

// The fallback for a server that hasn't shipped `available_columns`: infer the universe from what
// the rows in hand actually carry. Weaker — it can only ever offer a column already being returned —
// but the only real capture of this endpoint has no `available_columns`, and without this the
// chooser would go from partly working to blank against that server.
const displayKeysCarriedBy = (rows) => {
  const keys = new Set();
  (rows || []).forEach((row) => {
    if (row.sector !== null && row.sector !== undefined) keys.add('sector');
    if (row.marketCap !== null && row.marketCap !== undefined) keys.add('market_cap');
    if (row.hasMemberships) keys.add('memberships');
  });
  return keys;
};

// Offered and loaded are two different questions, and answering only the first is what put a column
// of "None" on screen: the universe lists is_member_of_sp500, so the chooser offered Indices, so the
// table drew it — over rows whose read never asked for a single flag, making the page assert "in no
// index" where the truth was "we never looked". OFFERED means the feed could emit it and the chooser
// may list it; LOADED means it is in THESE rows and the table may draw it.
export const availableOptionalColumns = (result) => {
  const universe = universeOf(result);
  const carried = displayKeysCarriedBy(result?.rows);
  // With no universe the rows are the only evidence there is, so offered and loaded coincide. That
  // is also why a result normalized before `requestedColumns` existed still renders.
  const offered = universe ? displayKeysNaming(universe) : carried;
  const requested = displayKeysNaming(requestedOf(result));
  return OPTIONAL_COLUMNS.filter((column) => offered.has(column.key)).map((column) => ({
    ...column,
    // Named in the request, or visible in the rows. The first is what lets a column the server
    // answered "null on every row" to still render — an asked-and-answered nothing is a fact, and
    // the unasked case is the one that must not reach the table.
    loaded: requested.has(column.key) || carried.has(column.key),
  }));
};

// The payload columns this feed says it has and this read did not ask for.
//
// A read that comes back 200 having quietly not carried the reasoning is the failure mode with no
// symptom: the rows are right, the table is right, and the disclosure is simply absent — which is
// indistinguishable from a score spec that declares no extra_columns at all. The dropped-columns
// notice cannot cover it, because that one is gated on the server having REFUSED the request.
//
// Only payload names, and deliberately so. Sector, market cap and the index flags can also be absent
// from a request, but only because the analyst turned them off in the chooser — reporting those as a
// gap would nag about a choice. A payload name is never in `hiddenKeys`: it is not a toggleable
// column, so its absence is never something anybody chose.
//
// An unknown universe yields nothing: with no `available_columns` there is no evidence the feed has
// a payload at all, and guessing one into a notice would invent a loss.
export const unrequestedPayloadColumns = (result) => {
  const universe = universeOf(result);
  if (!universe) return [];
  const requested = new Set(requestedOf(result) || []);
  return universe.filter((name) => isPayloadName(name) && !requested.has(name));
};

// Every column this feed can show, in render order, each tagged with whether the request can leave
// it out and whether these rows actually hold it. The chooser lists all of them — an offered column
// missing from the rows is exactly what the analyst needs a control for — so `loaded` rides along
// for whoever draws them.
export const allDisplayColumns = (result) => [
  // The floor and the per-input markers arrive whatever `columns` says, so for them the two
  // questions never come apart.
  ...CORE_COLUMNS.map((column) => ({ ...column, kind: 'core', omittable: false, loaded: true })),
  ...availableOptionalColumns(result).map((column) => ({
    key: column.key,
    label: column.label,
    kind: 'optional',
    omittable: true,
    loaded: column.loaded,
  })),
  ...(result?.memberColumns || []).map((column) => ({
    key: memberColumnKey(column.key),
    label: inputColumnLabel(column),
    kind: 'member',
    omittable: false,
    loaded: true,
  })),
];

// A table with no columns is a blank rectangle, so the last visible one can't be hidden. Counted
// over what the table actually DRAWS: an offered-but-unloaded column occupies a chip in the chooser
// and no space in the table, so letting it pad this count would permit hiding the last real column
// and leave the blank rectangle this guard exists to prevent.
export const canHideColumn = (result, hiddenKeys, key) => {
  if ((hiddenKeys || []).includes(key)) return true; // already hidden — this would be showing it again
  const columns = allDisplayColumns(result);
  // Turning off a column the table isn't drawing takes nothing off screen; it only stops the next
  // request naming it. The blank-table guard has no business refusing that.
  if (columns.some((column) => column.key === key && !column.loaded)) return true;
  const visible = columns.filter((column) => column.loaded && !(hiddenKeys || []).includes(column.key));
  return visible.length > 1;
};

// The floor columns the table will draw. They are never absent from the response, so the only thing
// that can remove one is the analyst turning it off.
export const visibleCoreColumns = (hiddenKeys = []) =>
  CORE_COLUMNS.filter((column) => !hiddenKeys.includes(column.key));

export const visibleMemberColumns = (memberColumns = [], hiddenKeys = []) =>
  memberColumns.filter((column) => !hiddenKeys.includes(memberColumnKey(column.key)));

// Offered by this feed, switched ON, and still not in these rows — because the read that fetched
// them never named them. Read #1 of an unseen feed cannot name an index flag: the names are
// feed-specific, and `columns` is an allowlist validated against a universe the page only learns
// FROM a response.
//
// The hidden ones are excluded on purpose, and that is the whole difference between this and a bare
// `!loaded` filter: a column the analyst switched off is absent because they said so, and offering
// to re-read for it would be advice against their own press.
//
// Deliberately NOT the same question as unrequestedPayloadColumns, which answers it for payload
// names — those are never toggleable, so their absence is never a choice, and they are not in the
// chooser to be counted here. The two sets are disjoint and both are gaps worth closing.
export const unloadedVisibleColumns = (result, hiddenKeys = []) =>
  allDisplayColumns(result).filter((column) => !column.loaded && !(hiddenKeys || []).includes(column.key));

// Which optional columns to RENDER: loaded, minus those the analyst turned off. The loaded test is
// what stops an offered-but-unasked-for column drawing formatMemberships([]) as "None" or a full
// column of em dashes — a table stating a fact about every company on the strength of a question
// nobody asked. Un-hiding one puts it back in the request, not on screen; the next read draws it.
export const visibleOptionalColumns = (result, hiddenKeys = []) =>
  availableOptionalColumns(result).filter((column) => column.loaded && !hiddenKeys.includes(column.key));

// What an optional column's cell says for a row. The formatters already decide how an absent value
// renders — an em dash for a sector or a market cap we don't have, "None" for a company in no index,
// which is a real answer rather than a gap — so this is only the mapping from a display key to the
// formatter that answers for it, and it lives beside the key rather than in an if-chain in the
// table.
// An unrecognized key returns null rather than a placeholder: a column the table drew but this
// module can't fill is a bug in the model, and a blank cell is the honest rendering of it.
export const optionalCellText = (row, columnKey) => {
  if (columnKey === 'sector') return formatSector(row.sector);
  if (columnKey === 'market_cap') return formatMarketCap(row.marketCap);
  if (columnKey === 'memberships') return formatMemberships(row.memberships);
  return null;
};

// The grid track list, and how many tracks it has, from the three groups the table draws in order.
//
// Both come out of one call because they were computed apart and disagreed: the table built its
// tracks from all three groups but sized the scroll container's min-width from the member columns
// alone. That undercounted a feed with enrichment columns and overcounted one whose members were
// hidden — wrong in both directions from the same expression — so the count is now simply how many
// tracks were emitted, which cannot drift from them.
export const tableGrid = (coreColumns = [], optionalColumns = [], memberColumns = []) => {
  const trackModels = [
    ...coreColumns.map((column) => trackOf(column)),
    ...optionalColumns.map((column) => trackOf(column)),
    ...memberColumns.map(() => MEMBER_COLUMN_TRACK),
  ];
  const tracks = trackModels.map(trackCss);
  // The scroll container's min-width is the sum of what the tracks themselves refuse to go below,
  // plus the gaps between them and the row's own padding. It is derived rather than approximated
  // because the approximation it replaces — a fixed base for the core plus a flat per-column figure
  // — went wrong the moment the count started including the core: the core was charged twice, and a
  // five-column table reserved 1170px of a 1140px page and scrolled sideways for nothing.
  const minWidth = trackModels.length
    ? trackModels.reduce((total, track) => total + track.minWidth, 0) +
      (trackModels.length - 1) * GRID_GAP +
      ROW_PADDING * 2
    : 0;
  return { template: tracks.join(' '), count: tracks.length, minWidth };
};

// The `columns` request field is ADDITIVE: omitting it asks for the floor alone
// (utc_knowable_at, ticker, company_id, score), and naming a column adds it on top. There is no drop
// operation on the wire, so this returns a keep-list on every read, not only when something is
// hidden. The old "omitted means everything, so send nothing unless dropping" reading was backwards,
// and it deadlocked the page: nothing hidden meant no `columns`, which meant floor-only rows, which
// meant the chooser found no enrichment to offer, which meant nothing could ever be hidden.
//
// Two states for the universe. Known (`availableColumns` from a previous read of THIS feed): keep
// everything in it, which is how spec-declared payload columns and company_name reach the response
// at all. Unknown (first read): guess with SPECULATIVE_COLUMNS, plus any index flags a previous read
// of this feed let us harvest, since those names are evidenced rather than guessed. A guess can name
// a column this feed doesn't have, which is a 422 — viewFeed retries once without `columns` for
// exactly that reason, The caller used to have to flag such a list as speculative; it no longer does, because
// the retry now gates on the server naming the `columns` field rather than on the caller saying so.
//
// company_name is not a toggleable display column and so is never in `hiddenKeys`: it survives the
// filter below on every call. That is the point — companyLabel degrades to "Company <id>" whenever
// the name is missing, which until now was always.
export const buildColumnsParam = ({
  availableColumns = null,
  hiddenKeys = [],
  indexFields = [],
  payloadFields = [],
  slim = false,
} = {}) => {
  const universe = universeOf({ availableColumns });

  // Index flags have to be enumerated by name to be asked for OR excluded, and the two sources of
  // names can disagree: the universe is authoritative, `indexFields` is what a
  // pre-available_columns server let us harvest from a previous read's rows. Take the union, and
  // take it as OFFERABLE, not merely as excludable. Treating it only as the latter is what left the
  // Indices toggle permanently inert against such a server — the flags were never in the keep-list,
  // so turning the toggle on asked for nothing and turning it off subtracted nothing.
  const membershipNames = new Set([
    ...(universe || []).filter(isIndexField),
    ...(indexFields || []).filter(isIndexField),
  ]);

  // Payload names are the one part of the universe that cannot be guessed — they are a score spec's
  // declared `extra_columns`, arbitrary per spec — so on a first read of an unseen feed they can only
  // come from outside the response. `payloadFields` is that outside source: the spec itself, resolved
  // from the feed id (see payloadColumns.js). Without it, read #1 asks for SPECULATIVE_COLUMNS alone,
  // the rows come back with no payload, and the reasoning disclosure has nothing to open.
  //
  // Unioned ONLY when the universe is unknown, unlike indexFields. A known universe is the
  // server's own answer about what this feed emits; the spec is a claim about what it SHOULD, and the
  // two can disagree — a spec edited after publish, or a re-cut publishing to an older feed name.
  // Naming a column outside the universe is a 422 on the whole read, which the page then caches as a
  // refusal of the `columns` key and pays for on every later read of that feed. So where there is an
  // authoritative answer, it wins outright.
  const offerable = universe
    ? [...universe.filter((name) => !ALWAYS_COLUMN_FIELDS.includes(name)), ...membershipNames]
    : [...SPECULATIVE_COLUMNS, ...(payloadFields || []).filter(isPayloadName), ...membershipNames];

  const dropped = new Set();
  if (hiddenKeys.includes('sector')) dropped.add('sector');
  if (hiddenKeys.includes('market_cap')) dropped.add('market_cap');
  if (hiddenKeys.includes('memberships')) membershipNames.forEach((name) => dropped.add(name));

  // Under `slim` the server empties this feed's payload keys, so a payload name is no longer in the
  // universe it validates `columns` against — and naming one is a 422 on the whole read, not a
  // silently ignored request. The names still sit in whatever universe an earlier NON-slim read of
  // this feed filed, which is where they would otherwise come from.
  //
  // The cost of getting this wrong is not one failed read. viewFeed rescues the 422 by re-reading
  // without `columns`, so the rows arrive and nothing looks broken — but the page files the refusal
  // and stops sending `columns` to this feed for the rest of the session, taking sector, market cap
  // and index membership with it on every LATER non-slim read. One tick of a checkbox, and the
  // enrichment is gone until the analyst finds the notice that undoes it.
  const survivesSlim = slim ? offerable.filter((name) => !isPayloadName(name)) : offerable;

  const keep = survivesSlim.filter((name, index) => !dropped.has(name) && survivesSlim.indexOf(name) === index);
  // Only an empty keep-list omits the field, and then it says the true thing: the floor is all that
  // was wanted.
  return keep.length ? keep : undefined;
};
